import { useState, type FormEvent } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { IconLock } from '@tabler/icons-react'
import { tokens } from '@/theme'
import { tenantAdminLogin, TenantAdminApiError } from '@/lib/tenantAdminApi'
import NotFound from '@/pages/NotFound'

/**
 * Tenant-admin sign-in for the GSA self-serve portal (ag-009).
 *
 * One admin account per tenant; the backend returns a generic 401 on any
 * failure (no slug/admin enumeration), so the error copy stays generic too.
 */
export default function TenantAdminLogin() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (!slug) return <NotFound />

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    tenantAdminLogin(slug, email.trim(), password)
      .then(() => navigate(`/g/${slug}/admin`, { replace: true }))
      .catch((err) => {
        setError(
          err instanceof TenantAdminApiError
            ? err.message
            : 'Sign-in failed. Check your connection and try again.',
        )
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: tokens.cream,
        display: 'flex',
        alignItems: 'center',
        py: 8,
      }}
    >
      <Container maxWidth="xs">
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={1} sx={{ alignItems: 'center', mb: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  bgcolor: tokens.green[50],
                  color: tokens.green.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconLock size={22} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                GSA staff sign-in
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Publish agendas, minutes, video links and announcements to your public page.
              </Typography>
            </Stack>

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            <form onSubmit={onSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                  autoFocus
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                <Button type="submit" variant="contained" disabled={submitting} fullWidth>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </Stack>
            </form>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3, textAlign: 'center' }}>
              <Link component={RouterLink} to={`/g/${slug}`} color="primary">
                ← Back to the public page
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
